/**
 * BatteryComparisonTab Component
 *
 * Third Energy tab: Fixed vs Agile vs Agile+Battery. Shows headline savings
 * tiles, a simulation-metadata line (coverage, cycles, computed-at), the
 * three-series chart, a collapsible battery-params panel, and a monthly
 * breakdown table.
 */

import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useBatterySimulation,
  DEFAULT_BATTERY,
} from '@/hooks/useBatterySimulation'
import {
  identifyPreset,
  getPreset,
  presetMidPrice,
  CUSTOM_PRESET_ID,
} from '@/lib/batteryPresets'
import BatteryComparisonChart from './BatteryComparisonChart'
import BatteryParametersPanel from './BatteryParametersPanel'

function formatGbp(pence, vatRate = 0) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format((pence / 100) * (1 + vatRate))
}

function formatGbp2(pence, vatRate = 0) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format((pence / 100) * (1 + vatRate))
}

function formatMonth(monthStr) {
  return new Date(monthStr).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function formatRelative(iso) {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diffMin = Math.round((now - then) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? '' : 's'} ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? '' : 's'} ago`
  const diffDay = Math.round(diffHr / 24)
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
}

/** Annualise a windowed saving: scale by 365 / days_in_window */
function annualise(pence, daysInWindow) {
  if (!daysInWindow) return 0
  return (pence / daysInWindow) * 365
}

function HeadlineTile({ label, value, sublabel, tone = 'neutral' }) {
  const toneClasses = {
    neutral: 'text-neutral-900',
    positive: 'text-green-600',
  }[tone]
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-neutral-500 uppercase tracking-wide">{label}</p>
        <p className={`text-3xl font-bold mt-2 ${toneClasses}`}>{value}</p>
        {sublabel && <p className="text-xs text-neutral-500 mt-1">{sublabel}</p>}
      </CardContent>
    </Card>
  )
}

function Headlines({ data, isLoading, vatRate = 0, preset }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    )
  }
  if (!data?.aggregate) return null

  const { total_fixed_pence, total_agile_pence, total_agile_battery_pence } = data.aggregate
  const daysInWindow = data.monthly.reduce((s, m) => s + (m.days_in_window ?? 0), 0)

  const tariffSaving = total_fixed_pence - total_agile_pence          // fixed → agile
  const batterySaving = total_agile_pence - total_agile_battery_pence  // agile → agile+battery

  const tariffAnnual = annualise(tariffSaving, daysInWindow)
  const batteryAnnual = annualise(batterySaving, daysInWindow)

  // Simple payback: hardware mid-price ÷ annual battery-specific saving
  // inc VAT. Excludes installation, commissioning, DNO fees, degradation,
  // finance. Only meaningful when a real preset is selected.
  const midPrice = presetMidPrice(preset)
  const batteryAnnualIncVat = (batteryAnnual / 100) * (1 + vatRate)
  const paybackYears = midPrice && batteryAnnualIncVat > 0
    ? midPrice / batteryAnnualIncVat
    : null

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex items-baseline justify-between">
            <p className="text-xs text-neutral-500 uppercase tracking-wide">∆ Tariff</p>
            <p className={`text-xl font-bold ${tariffSaving > 0 ? 'text-green-600' : 'text-neutral-900'}`}>
              {formatGbp(tariffAnnual, vatRate)}/yr
            </p>
          </div>
          <div className="flex items-baseline justify-between">
            <p className="text-xs text-neutral-500 uppercase tracking-wide">∆ Battery <span className="normal-case">(DP ceiling)</span></p>
            <p className="text-xl font-bold text-green-600">
              {formatGbp(batteryAnnual, vatRate)}/yr
            </p>
          </div>
          <div className="border-t border-neutral-200 pt-3 flex items-baseline justify-between">
            <p className="text-xs text-neutral-500 uppercase tracking-wide font-semibold">∆ Total</p>
            <p className="text-2xl font-bold text-green-600">
              {formatGbp(tariffAnnual + batteryAnnual, vatRate)}/yr
            </p>
          </div>
          <p className="text-[11px] text-neutral-400">
            {formatGbp2(tariffSaving + batterySaving, vatRate)} over {daysInWindow} days
          </p>
        </CardContent>
      </Card>
      <HeadlineTile
        label="Simple payback (hardware only)"
        value={paybackYears !== null ? `${paybackYears.toFixed(1)} yrs` : '—'}
        sublabel={preset
          ? `£${(preset.price_gbp_min / 1000).toFixed(0)}-${(preset.price_gbp_max / 1000).toFixed(0)}k hardware · excl install`
          : 'Select a preset for payback'}
        tone="neutral"
      />
    </div>
  )
}

function SimulationMeta({ data }) {
  if (!data) return null
  const monthly = data.monthly ?? []
  const totalMatched = monthly.reduce((s, m) => s + (m.slots_matched ?? 0), 0)
  const totalExpected = monthly.reduce((s, m) => s + (m.slots_expected ?? 0), 0)
  const coverage = totalExpected ? (100 * totalMatched / totalExpected) : 0

  const cycles = data.aggregate?.battery_full_cycles ?? 0
  const daysInWindow = monthly.reduce((s, m) => s + (m.days_in_window ?? 0), 0)
  const annualCycles = daysInWindow ? Math.round((cycles / daysInWindow) * 365) : 0

  return (
    <p className="text-xs text-neutral-500">
      {coverage.toFixed(1)}% coverage over {monthly.length} months ·{' '}
      {annualCycles} full cycles/yr ·{' '}
      {data.cached ? 'cached' : 'computed'} {formatRelative(data.computed_at)}
    </p>
  )
}

function MonthlyTable({ data, isLoading, vatRate = 0 }) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        </CardContent>
      </Card>
    )
  }
  const monthly = data?.monthly ?? []
  if (!monthly.length) return null

  const sorted = [...monthly].reverse()
  const totals = monthly.reduce((acc, m) => ({
    kwh: acc.kwh + (m.kwh_consumed ?? 0),
    fixed: acc.fixed + (m.fixed_total_pence ?? 0),
    agile: acc.agile + (m.agile_total_pence ?? 0),
    battery: acc.battery + (m.agile_battery_total_pence ?? 0),
    saving: acc.saving + (m.saving_vs_agile_pence ?? 0),
    throughput: acc.throughput + (m.battery_throughput_kwh ?? 0),
  }), { kwh: 0, fixed: 0, agile: 0, battery: 0, saving: 0, throughput: 0 })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 px-2 font-medium text-neutral-600">Month</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">kWh</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Fixed</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Agile</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Agile + Battery</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Battery saving</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Throughput (kWh)</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(m => (
                <tr key={m.month} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="py-2.5 px-2 font-medium text-neutral-900">{formatMonth(m.month)}</td>
                  <td className="py-2.5 px-2 text-right text-neutral-700">
                    {(m.kwh_consumed ?? 0).toFixed(0)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-neutral-700">
                    {formatGbp2(m.fixed_total_pence ?? 0, vatRate)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-neutral-700">
                    {formatGbp2(m.agile_total_pence ?? 0, vatRate)}
                  </td>
                  <td className="py-2.5 px-2 text-right font-medium text-neutral-900">
                    {formatGbp2(m.agile_battery_total_pence ?? 0, vatRate)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-green-600 font-medium">
                    {formatGbp2(m.saving_vs_agile_pence ?? 0, vatRate)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-neutral-500">
                    {(m.battery_throughput_kwh ?? 0).toFixed(0)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-neutral-300">
                <td className="py-3 px-2 font-bold text-neutral-900">Total</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{totals.kwh.toFixed(0)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbp2(totals.fixed, vatRate)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbp2(totals.agile, vatRate)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbp2(totals.battery, vatRate)}</td>
                <td className="py-3 px-2 text-right font-bold text-green-600">{formatGbp2(totals.saving, vatRate)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{totals.throughput.toFixed(0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

export default function BatteryComparisonTab({ vatRate = 0, tariff, tariffLabel, onTariffChange }) {
  // Local state for the currently-applied battery params; param panel edits
  // are staged in its own internal draft and only lifted up on Apply.
  const [battery, setBattery] = useState(DEFAULT_BATTERY)

  const { data, isLoading, error } = useBatterySimulation({ battery, tariff })

  // Which preset (if any) matches the currently-applied battery params?
  // Used to show payback + preset-aware labels on the headline tiles.
  const appliedPreset = useMemo(() => {
    const id = identifyPreset(battery)
    return id === CUSTOM_PRESET_ID ? null : getPreset(id)
  }, [battery])

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-800">Failed to run battery simulation: {error}</p>
          </CardContent>
        </Card>
      )}

      <Headlines data={data} isLoading={isLoading} vatRate={vatRate} preset={appliedPreset} />
      <SimulationMeta data={data} />

      <BatteryComparisonChart
        data={data}
        isLoading={isLoading}
        vatRate={vatRate}
        tariff={tariff}
        tariffLabel={tariffLabel}
        onTariffChange={onTariffChange}
      />

      <BatteryParametersPanel
        battery={battery}
        onApply={setBattery}
        isLoading={isLoading}
      />

      <MonthlyTable data={data} isLoading={isLoading} vatRate={vatRate} />
    </div>
  )
}
